import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert, KeyRound, Plus, Copy, Trash2 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import cultivarService from '../../services/cultivar';
import type { CultivarOrderApiKey } from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

interface LicenseOption {
  id: number;
  cultivar_id: number;
  cultivar_name?: string | null;
  region?: string | null;
  country?: string | null;
}

function OrderApiKeys() {
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [keys, setKeys] = useState<CultivarOrderApiKey[]>([]);
  const [licenses, setLicenses] = useState<LicenseOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [licenseId, setLicenseId] = useState<number | ''>('');
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ id: number; plaintext: string } | null>(null);
  const [revoking, setRevoking] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [keysResp, licensesResp] = await Promise.all([
        cultivarService.listOrderApiKeys(),
        cultivarService.listLicenses({ role: 'licensee', license_tier: 'nursery_propagation', status: 'active', limit: 100 }),
      ]);
      if (keysResp.success && keysResp.data) setKeys(keysResp.data as CultivarOrderApiKey[]);
      if (licensesResp.success && licensesResp.data) {
        // shape: { licenses: [...] } or array; normalise.
        const raw = licensesResp.data as unknown;
        const list: unknown[] = Array.isArray(raw) ? raw : ((raw as { licenses?: unknown[] }).licenses ?? []);
        setLicenses(list as LicenseOption[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!licenseId) { setError('Choose a nursery sublicence first.'); return; }
    setCreating(true);
    setError(null);
    setInfo(null);
    try {
      const resp = await cultivarService.createOrderApiKey({
        regional_license_id: licenseId as number,
        label: label || null,
      });
      if (!resp.success) throw new Error(resp.error?.message || 'Create failed');
      if (!resp.data) throw new Error('Create failed');
      const created = resp.data as CultivarOrderApiKey & { plaintext: string };
      setRevealedKey({ id: created.id, plaintext: created.plaintext });
      setShowCreate(false);
      setLabel('');
      setLicenseId('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: number) => {
    setRevoking(id);
    setError(null);
    setInfo(null);
    try {
      const resp = await cultivarService.revokeOrderApiKey(id);
      if (!resp.success) throw new Error(resp.error?.message || 'Revoke failed');
      setInfo('Key revoked.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revoke failed');
    } finally {
      setRevoking(null);
    }
  };

  if (licenseLoading || loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div><h3 className="font-semibold">Cultivar PBR seat required</h3></div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Cultivar ordering API keys"
        subtitle="Keys for your nursery's own platform to POST cultivar orders into HHNode via the public API."
        icon={<KeyRound className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Orders
          </Button>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" /> New API key
          </Button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}
        {info && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm">{info}</div>}

        {revealedKey && (
          <Card>
            <CardContent className="p-4 bg-amber-50 border-amber-200">
              <h3 className="font-semibold text-amber-900">Save this key now — it won't be shown again.</h3>
              <p className="text-sm text-amber-800 mt-1">Copy it into your platform's configuration. If you lose it, revoke this key and mint a new one.</p>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 bg-white border rounded px-2 py-1.5 text-sm break-all">{revealedKey.plaintext}</code>
                <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(revealedKey.plaintext)}>
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setRevealedKey(null)}>Dismiss</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Label</th>
                  <th className="px-4 py-2">Cultivar / region</th>
                  <th className="px-4 py-2">Prefix</th>
                  <th className="px-4 py-2">Last used</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No API keys yet.</td></tr>
                )}
                {keys.map((k) => (
                  <tr key={k.id} className="border-t">
                    <td className="px-4 py-3 font-medium">{k.label}</td>
                    <td className="px-4 py-3">
                      {k.cultivar_name || `Cultivar #${k.cultivar_id}`}
                      {(k.region || k.country) && <div className="text-xs text-gray-500">{[k.region, k.country].filter(Boolean).join(' / ')}</div>}
                    </td>
                    <td className="px-4 py-3"><code className="text-xs">{k.key_prefix}…</code></td>
                    <td className="px-4 py-3 text-gray-600">{k.last_used_at ? k.last_used_at.slice(0, 10) : '—'}</td>
                    <td className="px-4 py-3">
                      {k.revoked_at
                        ? <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Revoked</span>
                        : <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Active</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!k.revoked_at && (
                        <Button variant="ghost" size="sm" onClick={() => revoke(k.id)} disabled={revoking === k.id}
                          className="text-error-600 hover:bg-error-50">
                          {revoking === k.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {showCreate && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold">New cultivar ordering API key</h2>
                <p className="text-xs text-gray-500 mt-1">Pick the nursery sublicence the key will act on behalf of.</p>
              </div>
              <div className="px-6 py-4 space-y-3">
                <label className="text-sm block">
                  <span className="text-gray-700">Nursery sublicence</span>
                  <select value={licenseId} onChange={(e) => setLicenseId(e.target.value ? parseInt(e.target.value, 10) : '')}
                    className="mt-1 w-full border rounded px-2 py-1.5">
                    <option value="">— Choose —</option>
                    {licenses.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.cultivar_name || `Cultivar #${l.cultivar_id}`}{(l.region || l.country) ? ` (${[l.region, l.country].filter(Boolean).join('/')})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm block">
                  <span className="text-gray-700">Label (e.g. "Acme nursery platform")</span>
                  <input value={label} onChange={(e) => setLabel(e.target.value)}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
              </div>
              <div className="px-6 py-3 border-t flex justify-end gap-2 bg-gray-50">
                <Button variant="secondary" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
                <Button onClick={create} disabled={creating || !licenseId}>
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default OrderApiKeys;
